/**
 * Cross-platform key/value storage adapter.
 *
 * Web:    reads/writes `localStorage` directly (synchronous, unchanged behaviour).
 * Native: the underlying Capacitor Preferences API is ASYNC, but the rest of the
 *         app (notably services/api/core.ts) reads the auth token synchronously.
 *         We bridge the gap with an in-memory cache: call `hydrateStorage()` once
 *         at app boot to populate the cache from persistent storage, after which
 *         reads are synchronous and writes are mirrored back asynchronously.
 *
 * SECURITY NOTE: @capacitor/preferences uses UserDefaults (iOS) / SharedPreferences
 * (Android), which are NOT hardware-encrypted. For GA, migrate the auth-token keys
 * to a Keychain/Keystore-backed plugin. Tracked in the plan's Risks section.
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const isNative = Capacitor.isNativePlatform();

/** Keys persisted across launches on native (loaded eagerly by hydrateStorage). */
const PERSISTENT_KEYS = [
  'buzzbit_token',
  'buzzbit_refresh_token',
  'buzzbit_workspace',
  'buzzbit_admin_token',
] as const;

const cache = new Map<string, string>();

/**
 * Load persisted values into the in-memory cache. No-op on web (localStorage is
 * already synchronous). MUST be awaited before the app reads auth state on native.
 */
export async function hydrateStorage(): Promise<void> {
  if (!isNative) return;
  await Promise.all(
    PERSISTENT_KEYS.map(async (key) => {
      const { value } = await Preferences.get({ key });
      if (value != null) cache.set(key, value);
    }),
  );
}

export function storageGet(key: string): string | null {
  if (isNative) return cache.has(key) ? (cache.get(key) as string) : null;
  return localStorage.getItem(key);
}

export function storageSet(key: string, value: string): void {
  if (isNative) {
    cache.set(key, value);
    void Preferences.set({ key, value });
  } else {
    localStorage.setItem(key, value);
  }
}

export function storageRemove(key: string): void {
  if (isNative) {
    cache.delete(key);
    void Preferences.remove({ key });
  } else {
    localStorage.removeItem(key);
  }
}
